import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["menu", "icon"]

  connect() {
    this.opened = false
    this.hide = this.hide.bind(this)
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
    this.handleWindowResize = this.close.bind(this)
    this.handleWindowScroll = this.handleWindowScroll.bind(this)
    this.handleCloseAll = this.closeFromEvent.bind(this)
    this.handleMenuClick = this.close.bind(this)
    this.menu = this.menuTarget
    this.originalMenuParent = this.menu.parentNode
    this.originalMenuNextSibling = this.menu.nextSibling

    document.addEventListener("click", this.hide)
    document.addEventListener("keydown", this.handleDocumentKeydown)
    document.addEventListener("dropdown:close-all", this.handleCloseAll)
    window.addEventListener("resize", this.handleWindowResize)
    window.addEventListener("scroll", this.handleWindowScroll, true)
    this.menu.addEventListener("click", this.handleMenuClick)
  }

  disconnect() {
    document.removeEventListener("click", this.hide)
    document.removeEventListener("keydown", this.handleDocumentKeydown)
    document.removeEventListener("dropdown:close-all", this.handleCloseAll)
    window.removeEventListener("resize", this.handleWindowResize)
    window.removeEventListener("scroll", this.handleWindowScroll, true)
    this.menu.removeEventListener("click", this.handleMenuClick)
    clearTimeout(this.timeout)
    this.restoreMenu()
  }

  toggle(event) {
    event.stopPropagation()

    if (!this.opened) {
      document.dispatchEvent(new CustomEvent("dropdown:close-all", { detail: { except: this.element } }))
      this.open()
    } else {
      this.close()
    }
  }

  closeFromEvent(event) {
    if (event.detail?.except === this.element) return

    this.close()
  }

  close() {
    this.opened = false
    if (this.hasIconTarget) this.iconTarget.classList.remove("rotate-180")
    this.menu.classList.remove("opacity-100", "scale-100", "pointer-events-auto")
    this.menu.classList.add("opacity-0", "scale-95", "pointer-events-none")
    this.updateExpandedState(false)

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.menu.classList.add("hidden")
      this.restoreMenu()
    }, 200)
  }

  open() {
    this.opened = true
    clearTimeout(this.timeout)
    if (this.hasIconTarget) this.iconTarget.classList.add("rotate-180")
    this.portalMenu()
    this.menu.classList.remove("hidden")
    this.positionMenu()
    requestAnimationFrame(() => {
      this.menu.classList.remove("opacity-0", "scale-95", "pointer-events-none")
      this.menu.classList.add("opacity-100", "scale-100", "pointer-events-auto")
    })
    this.updateExpandedState(true)
  }

  hide(event) {
    if (this.element.contains(event.target) || this.menu.contains(event.target)) return

    this.close()
  }

  handleDocumentKeydown(event) {
    if (event.key === "Escape" && this.opened) {
      event.preventDefault()
      this.close()
      this.triggerElement?.focus()
      return
    }

    if (!["ArrowDown", "ArrowUp"].includes(event.key) || event.target !== this.triggerElement) return

    event.preventDefault()
    if (!this.opened) {
      document.dispatchEvent(new CustomEvent("dropdown:close-all", { detail: { except: this.element } }))
      this.open()
    }

    const items = this.menu.querySelectorAll("[role^='menuitem']")
    const item = event.key === "ArrowUp" ? items[items.length - 1] : items[0]
    item?.focus()
  }

  handleWindowScroll() {
    if (this.opened) this.positionMenu()
  }

  updateExpandedState(expanded) {
    const trigger = this.triggerElement
    if (trigger) trigger.setAttribute("aria-expanded", String(expanded))
  }

  portalMenu() {
    if (this.menu.parentNode === document.body) return

    document.body.append(this.menu)
  }

  restoreMenu() {
    this.menu.removeAttribute("style")
    if (this.menu.parentNode !== document.body) return

    if (this.originalMenuParent?.isConnected) {
      this.originalMenuParent.insertBefore(this.menu, this.originalMenuNextSibling)
    } else {
      this.menu.remove()
    }
  }

  positionMenu() {
    const trigger = this.triggerElement
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    const configuredWidth = this.menu.dataset.dropdownWidth
    const triggerWidth = configuredWidth === "trigger"
    const explicitWidth = Number(configuredWidth)
    const menuWidth = triggerWidth ? rect.width : (explicitWidth || this.menu.offsetWidth || 176)
    const menuLeft = triggerWidth ? rect.left : Math.max(8, rect.right - menuWidth)
    const menuOffset = Number(this.menu.dataset.dropdownOffset || 4)
    const menuZIndex = this.menu.dataset.dropdownZIndex || "60"
    const viewportPadding = 8
    const menuHeight = this.menu.scrollHeight
    const spaceBelow = window.innerHeight - rect.bottom - menuOffset - viewportPadding
    const spaceAbove = rect.top - menuOffset - viewportPadding
    const openAbove = menuHeight > spaceBelow && spaceAbove > spaceBelow
    const availableHeight = Math.max(120, openAbove ? spaceAbove : spaceBelow)
    const renderedHeight = Math.min(menuHeight, availableHeight)
    const menuTop = openAbove ? Math.max(viewportPadding, rect.top - menuOffset - renderedHeight) : rect.bottom + menuOffset

    Object.assign(this.menu.style, {
      position: "fixed",
      left: `${menuLeft}px`,
      top: `${menuTop}px`,
      width: `${menuWidth}px`,
      maxHeight: `${availableHeight}px`,
      overflowY: "auto",
      transformOrigin: openAbove ? "bottom right" : "top right",
      zIndex: menuZIndex
    })
  }

  get triggerElement() {
    return this.element.querySelector("[aria-haspopup='true']")
  }
}
